// The add/edit Event form on Game Detail — extracted verbatim (no behavior
// change) since it's a large, self-contained block driven entirely by the
// `eventForm` draft object + a handful of callbacks passed down from the page.
export function EventForm({
  eventForm, setEventForm, players, events, game, inputCls,
  COACH_RECIPIENT_EVENT_TYPES, formatEventTime, getCurrentPlaybackTime,
  onUseCurrentTime, onCancel, onSave,
}) {
  const hasVideo = !!(game.video_url || game.video_upload_id || game.bunny_video_id);
  return (
    <div className="bg-[#0A0A0A] border border-[#CCFF00]/40 rounded-sm p-4 mb-4 space-y-3" data-testid="event-form">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <label className="font-label text-xs text-zinc-400 block mb-2">Type</label>
          <select
            value={eventForm.type}
            onChange={(e) => {
              const newType = e.target.value;
              const coachEligible = COACH_RECIPIENT_EVENT_TYPES.includes(newType);
              setEventForm({ ...eventForm, type: newType, recipient_type: coachEligible ? eventForm.recipient_type : "player" });
            }}
            data-testid="event-form-type"
            className={inputCls}
          >
            <option value="goal">Goal</option>
            <option value="shot">Shot</option>
            <option value="save">Save</option>
            <option value="yellow_card">Yellow Card</option>
            <option value="red_card">Red Card</option>
            <option value="highlight">Highlight</option>
          </select>
        </div>
        {eventForm.type !== "save" && eventForm.type !== "highlight" && (
          <div>
            <label className="font-label text-xs text-zinc-400 block mb-2">Team</label>
            <select
              value={eventForm.team}
              onChange={(e) => setEventForm({ ...eventForm, team: e.target.value, player_id: e.target.value === "opposing_team" ? "" : eventForm.player_id })}
              data-testid="event-form-team"
              className={inputCls}
            >
              <option value="our_team">Us</option>
              <option value="opposing_team">Opposing team</option>
            </select>
          </div>
        )}
        {(eventForm.team === "our_team" || eventForm.type === "save" || eventForm.type === "highlight") && (
          <div>
            <label className="font-label text-xs text-zinc-400 block mb-2">{eventForm.type === "save" ? "Goalkeeper" : "Player"}</label>
            <select
              value={eventForm.recipient_type === "coach" ? "__coach__" : eventForm.player_id}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "__coach__") setEventForm({ ...eventForm, recipient_type: "coach", player_id: "" });
                else setEventForm({ ...eventForm, recipient_type: "player", player_id: v });
              }}
              data-testid="event-form-player"
              className={inputCls}
            >
              <option value="">Unknown</option>
              {COACH_RECIPIENT_EVENT_TYPES.includes(eventForm.type) && <option value="__coach__" data-testid="event-form-player-coach-option">Coach</option>}
              {players.map((p) => <option key={p.id} value={p.id} className="bg-[#121212]">#{p.jersey_number} {p.first_name} {p.last_name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="font-label text-xs text-zinc-400 block mb-2">Time (MM:SS)</label>
          <input type="text" inputMode="numeric" placeholder="MM:SS" value={eventForm.timestamp} onChange={(e) => setEventForm({ ...eventForm, timestamp: e.target.value })} data-testid="event-form-timestamp" className={inputCls} />
        </div>
        <div>
          <label className="font-label text-xs text-zinc-400 block mb-2">Seconds Prior</label>
          <input type="number" min="0" max="120" value={eventForm.pre_roll_seconds} onChange={(e) => setEventForm({ ...eventForm, pre_roll_seconds: e.target.value })} data-testid="event-form-pre-roll" className={inputCls} />
        </div>
        <div>
          <label className="font-label text-xs text-zinc-400 block mb-2">Seconds After</label>
          <input type="number" min="0" max="120" value={eventForm.post_roll_seconds} onChange={(e) => setEventForm({ ...eventForm, post_roll_seconds: e.target.value })} data-testid="event-form-post-roll" className={inputCls} />
        </div>
        {eventForm.type === "shot" && (
          <div>
            <label className="font-label text-xs text-zinc-400 block mb-2">Outcome</label>
            <select value={eventForm.outcome} onChange={(e) => setEventForm({ ...eventForm, outcome: e.target.value })} data-testid="event-form-outcome" className={inputCls}>
              <option value="">—</option>
              <option value="on_target">On target</option>
              <option value="off_target">Off target</option>
              <option value="saved">Saved</option>
            </select>
          </div>
        )}
        {eventForm.type === "goal" && (
          <div>
            <label className="font-label text-xs text-zinc-400 block mb-2">Assist (optional)</label>
            <select
              value={eventForm.assist_team}
              onChange={(e) => setEventForm({ ...eventForm, assist_team: e.target.value, assist_player_id: e.target.value === "opposing_team" ? "" : eventForm.assist_player_id })}
              data-testid="event-form-assist-team"
              className={inputCls}
            >
              <option value="">No assist</option>
              <option value="our_team">One of ours</option>
              <option value="opposing_team">Opposing team</option>
            </select>
          </div>
        )}
        {eventForm.type === "goal" && eventForm.assist_team === "our_team" && (
          <div>
            <label className="font-label text-xs text-zinc-400 block mb-2">Assist player</label>
            <select value={eventForm.assist_player_id} onChange={(e) => setEventForm({ ...eventForm, assist_player_id: e.target.value })} data-testid="event-form-assist-player" className={inputCls}>
              <option value="">Unknown</option>
              {players.map((p) => <option key={p.id} value={p.id} className="bg-[#121212]">#{p.jersey_number} {p.first_name} {p.last_name}</option>)}
            </select>
          </div>
        )}
        {eventForm.type === "save" && (
          <div>
            <label className="font-label text-xs text-zinc-400 block mb-2">Linked shot (optional)</label>
            <select value={eventForm.linked_shot_event_id} onChange={(e) => setEventForm({ ...eventForm, linked_shot_event_id: e.target.value })} data-testid="event-form-linked-shot" className={inputCls}>
              <option value="">None</option>
              {events.filter((e) => e.type === "shot").map((e) => (
                <option key={e.id} value={e.id} className="bg-[#121212]">{formatEventTime(e.timestamp)} — {e.team === "our_team" ? "Us" : "Opposing"} shot{e.outcome ? ` (${e.outcome.replace("_", " ")})` : ""}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      {hasVideo && (
        <button
          type="button"
          onClick={() => setEventForm({ ...eventForm, timestamp: formatEventTime(Math.floor(getCurrentPlaybackTime() || 0)) })}
          data-testid="event-form-use-current-time"
          className="text-xs text-[#CCFF00] hover:underline"
        >
          Use current video time
        </button>
      )}
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} data-testid="event-form-cancel" className="font-label text-xs px-4 py-2 rounded-sm text-zinc-400 hover:text-white transition-colors duration-150">Cancel</button>
        <button type="button" onClick={onSave} data-testid="event-form-save" className="font-label text-xs px-4 py-2 rounded-sm bg-[#CCFF00] text-black hover:bg-[#B3E600] transition-colors duration-150">{eventForm.id ? "Save changes" : "Add event"}</button>
      </div>
    </div>
  );
}
